import {
  Add,
  Clear,
  Delete,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import {
  Field,
  FieldArray,
  FieldInputProps,
  FieldMetaProps,
  Formik,
  useFormikContext,
} from "formik";
import { useContext, useRef, useState } from "react";
import * as Yup from "yup";
import { ObjectShape } from "yup";
import {
  AutomationConfig,
  ExtConfig,
  HooksConfig,
  ConfigItem as Item,
  ScheduleConfig,
  Tool,
  ToolConfig,
} from "../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import Avatar from "../Avatar";
import ScheduledTask from "../tasks/ScheduledTask";
import { OrderAdornment } from "./OrderAdornment";
import Morselbar from "../Morselbar";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

export default function ItemForm(props: {
  config:
    | ExtConfig
    | ToolConfig
    | ScheduleConfig
    | HooksConfig
    | AutomationConfig;
  variant: string;
  showTask?: boolean;
}) {
  const { config, variant, showTask = false } = props;

  const shape: ObjectShape = {};

  if (config.item.tool !== undefined && config.item.tool.contract_id === null)
    (config.item.tool as unknown as { contract_id: string }).contract_id = "";

  if (config.item.client !== undefined)
    shape.client = Yup.object().shape({
      url: Yup.string().url().required(),
      user: Yup.string().required(),
      pass: Yup.string().required(),
      domain: Yup.string().optional(),
    });

  if (config.item.client_api_key !== undefined)
    shape.client_api_key = Yup.object().shape({
      url: Yup.string().url().required(),
      api_key: Yup.string().required(),
    });

  if (config.item.simple_client !== undefined)
    shape.simple_client = Yup.object({
      url: Yup.string().url().required(),
    });

  if (config.item.email !== undefined)
    shape.email = Yup.object().shape({
      user: Yup.string().required(),
      pass: Yup.string().required(),
      domain: Yup.string().required(),
      sender: Yup.string().email().required(),
      base_url: Yup.string().required(),
      admin_email: Yup.string().email().required(),
    });

  if (config.item.groups !== undefined)
    shape.groups = Yup.object().shape({
      primary: Yup.array().of(Yup.string().required()),
      offboard: Yup.array().of(Yup.string().required()),
    });

  if (config.item.groups_jsm !== undefined)
    shape.groups_jsm = Yup.object().shape({
      primary: Yup.array().of(Yup.string().required()),
      offboard: Yup.array().of(Yup.string().required()),
    });

  if (config.item.entra_client !== undefined)
    shape.entra_client = Yup.object().shape({
      login_url: Yup.string().url().required(),
      api_url:Yup.string().url().required(),
      client_id: Yup.string().required(),
      client_secret: Yup.string().required(),
      tenant_id: Yup.string().required(),
      scope: Yup.string().required(),
      grant_type: Yup.string().required(),
    });
  
  if (config.item.mim_group !== undefined)
    shape.mim_group = Yup.object().shape({
      id: Yup.string().uuid().required(),
    });

  if (config.item.multiple_mim_groups !== undefined)
    shape.multiple_mim_groups = Yup.object().shape({
      read_only_group: Yup.string().uuid().required(),
      licensed_group: Yup.string().uuid().required(),
    });

  if (config.item.tool !== undefined)
    shape.tool = Yup.object().shape({
      display_name: Yup.string(),
      short_desc: Yup.string(),
      long_desc: Yup.string(),
      pricing: Yup.object().shape({
        desc: Yup.string(),
      }),
      contract_id: Yup.number().min(0),
      links: Yup.object().shape({
        main: Yup.string().url(),
      }),
    });

  if (config.item.schedule !== undefined)
    shape.schedule = Yup.object().shape({
      interval_seconds: Yup.number().min(0).required(),
    });

  if (config.item.mim_details !== undefined)
    shape.mim_details = Yup.object().shape({
      owner_id: Yup.string().uuid().required(),
      company_id: Yup.string().uuid().required(),
    });

  if (config.item.hooks_vom !== undefined)
    shape.hooks_vom = Yup.object().shape({
      verification_token: Yup.string(),
      secret: Yup.string(),
    });

  if (config.item.links !== undefined) {
    shape.links = Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        url: Yup.string().url().required(),
      })
    );
  }

  if (config.item.jira_details !== undefined) {
    shape.jira_details = Yup.object().shape({
      directory_id: Yup.number().positive(),
      role_read: Yup.string().required(),
      role_write: Yup.string().required(),
    });
  }

  if (config.item.jira_accounts !== undefined) {
    shape.jira_accounts = Yup.object().shape({
      sdop_user: Yup.string(),
      sdop_pass: Yup.string(),
      vcdm_user: Yup.string(),
      vcdm_pass: Yup.string(),
    });
  }

  if (config.item.polaris_details !== undefined) {
    shape.polaris_details = Yup.object().shape({
      organization_id: Yup.string().required(),
    });
  }

  if (config.item.automation !== undefined) {
    shape.automation = Yup.object().shape({
      api_key: Yup.string().required(),
    });
  }

  if (config.item.tableau_details !== undefined) {
    shape.tableau_details = Yup.object().shape({
      pat_name: Yup.string().required(),
      pat_secret: Yup.string().required(),
      pat_site_content_url: Yup.string().required(),
      pat_expiration_date: Yup.date().required(),
      onboarding_group: Yup.string().uuid().required(),
    });
  }

  if (config.item.caroline !== undefined) {
    shape.caroline = Yup.object().shape({
      azure_chat_url: Yup.string().url().required(),
      azure_chat_key: Yup.string().required(),
      azure_chat_deployment: Yup.string().required(),
      azure_chat_api_version: Yup.string().required(),
      azure_chat_embedding_deployment: Yup.string().required(),
      azure_search_url: Yup.string().url().required(),
      azure_search_key: Yup.string().required(),
      azure_search_index: Yup.string().required(),
      azure_container_url: Yup.string().url().required(),
      azure_container_sas_key: Yup.string().required(),
      azure_container_name: Yup.string().required(),
      azure_indexer_name: Yup.string().required(),
      azure_indexer_api_version: Yup.string().required(),
      azure_dalle_deployment: Yup.string().required(),
      azure_dalle_api_version: Yup.string().required(),
      slack_app_secret: Yup.string().required(),
      slack_user_token: Yup.string().required(),
      slack_bot_token: Yup.string().required(),
      slack_bot_id: Yup.string().required(),
      slack_app_user_token: Yup.string().required(),
      slack_channels_whitelsit: Yup.array().of(Yup.string()),
    });
  }

  const schema = Yup.object().shape(shape);

  const client = useContext(HttpClientContext);

  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    error?: string;
    message: string;
  }>({
    message: "",
  });

  const handleSubmit = (item: Item) => {
    setLoading(true);
    setOpen(false);
    setSubmitStatus({ message: "" });

    const payload = structuredClone(item) as unknown as {
      tool: { contract_id: string | number | null };
    };

    if (Boolean(payload.tool) && payload.tool.contract_id === "") {
      payload.tool.contract_id = null;
    } else if (Boolean(payload.tool))
      payload.tool.contract_id = parseInt(payload.tool.contract_id as string);

    config.item = payload as unknown as Item;

    client
      ?.post(`/api/${variant}`, config)
      .then(() => {
        setSubmitStatus({
          error: undefined,
          message: `${config.kind} config updated`,
        });
        setOpen(true);
      })
      .catch((e: AxiosError) => {
        setSubmitStatus({
          error: e.response?.headers[X_REQUEST_ID],
          message: `Failed to update config for ${config.kind}`,
        });
        setOpen(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Typography variant="h4">{config.kind}</Typography>

      {showTask && <ScheduledTask schedule={(config as ScheduleConfig).kind} />}

      <Formik
        initialValues={config.item}
        onSubmit={(values) => handleSubmit(values)}
        validationSchema={schema}
      >
        {({ values, handleSubmit }) => (
          <Box
            component="form"
            onSubmit={(...args) => {
              handleSubmit(...args);
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              width: "100%",
              maxWidth: "sm",
            }}
          >
            {values.client !== undefined && <ItemClient loading={loading} />}

            {values.client_api_key !== undefined && (
              <ItemClientApiKey loading={loading} />
            )}

            {values.simple_client !== undefined && (
              <ItemSimpleClient loading={loading} />
            )}

            {values.email !== undefined && <ItemEmail loading={loading} />}

            {values.groups !== undefined && (
              <>
                {values.groups.primary !== undefined && (
                  <ItemGroups
                    fieldName="groups"
                    groupName="primary"
                    loading={loading}
                  />
                )}
                {values.groups.offboard !== undefined && (
                  <ItemGroups
                    fieldName="groups"
                    groupName="offboard"
                    loading={loading}
                  />
                )}
              </>
            )}

            {values.groups_jsm !== undefined && (
              <>
                {values.groups_jsm.primary !== undefined && (
                  <ItemGroups
                    displayName="JSM"
                    fieldName="groups_jsm"
                    groupName="primary"
                    loading={loading}
                  />
                )}
                {values.groups_jsm.offboard !== undefined && (
                  <ItemGroups
                    displayName="JSM"
                    fieldName="groups_jsm"
                    groupName="offboard"
                    loading={loading}
                  />
                )}
              </>
            )}

            {values.mim_group !== undefined && (
              <ItemMimGroup loading={loading} />
            )}

            {values.multiple_mim_groups !== undefined && (
              <>
                <ItemMultipleMimGroup
                  group_type="read_only_group"
                  loading={loading}
                />
                <ItemMultipleMimGroup
                  group_type="licensed_group"
                  loading={loading}
                />
              </>
            )}

            {values.tool !== undefined && (
              <ItemTool loading={loading} kind={config.kind as Tool} />
            )}

            {values.schedule !== undefined && (
              <ItemSchedule loading={loading} />
            )}

            {values.mim_details !== undefined && (
              <ItemMimDetails loading={loading} />
            )}

            {values.hooks_vom !== undefined && (
              <ItemHooksVom loading={loading} />
            )}

            {values.links !== undefined && <ItemLinks loading={loading} />}

            {values.jira_details !== undefined && (
              <ItemJiraDetails loading={loading} />
            )}

            {values.jira_accounts !== undefined && (
              <ItemJiraAccounts loading={loading} />
            )}

            {values.polaris_details !== undefined && (
              <ItemPolarisDetails loading={loading} />
            )}

            {values.automation !== undefined && (
              <ItemAutomation loading={loading} />
            )}

            {values.entra_client !== undefined && (
              <ItemEntra loading={loading} />
            )}

            {values.tableau_entra_groups !== undefined && (
              <ItemTableauEntraGroups loading={loading} />
            )}

            {values.tableau_details !== undefined && (
              <ItemTableauDetails loading={loading} />
            )}

            {values.caroline !== undefined && (
              <ItemCaroline loading={loading} />
            )}

            <FormControl>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loading}
              >
                Submit
              </LoadingButton>
            </FormControl>
            <br />
          </Box>
        )}
      </Formik>

      <Morselbar
        open={open}
        setOpen={setOpen}
        content={submitStatus.message}
        error={submitStatus.error}
      />
    </>
  );
}

function ItemCaroline(props: { loading: boolean }) {
  const loading = props.loading;

  const { values, getFieldMeta, handleChange } = useFormikContext<Item>();

  const azure_chat_url = getFieldMeta("caroline.azure_chat_url");
  const azure_chat_key = getFieldMeta("caroline.azure_chat_key");
  const azure_chat_deployment = getFieldMeta("caroline.azure_chat_deployment");
  const azure_chat_api_version = getFieldMeta(
    "caroline.azure_chat_api_version"
  );
  const azure_chat_embedding_deployment = getFieldMeta(
    "caroline.azure_chat_embedding_deployment"
  );

  const azure_search_url = getFieldMeta("caroline.azure_search_url");
  const azure_search_key = getFieldMeta("caroline.azure_search_key");
  const azure_search_index = getFieldMeta("caroline.azure_search_index");

  const azure_dalle_deployment = getFieldMeta(
    "caroline.azure_dalle_deployment"
  );
  const azure_dalle_api_version = getFieldMeta(
    "caroline.azure_dalle_api_version"
  );
  const azure_container_url = getFieldMeta("caroline.azure_container_url");
  const azure_container_name = getFieldMeta("caroline.azure_container_name");
  const azure_container_sas_key = getFieldMeta(
    "caroline.azure_container_sas_key"
  );
  const azure_indexer_name = getFieldMeta("caroline.azure_indexer_name");
  const azure_indexer_api_version = getFieldMeta(
    "caroline.azure_indexer_api_version"
  );

  const slack_app_secret = getFieldMeta("caroline.slack_app_secret");
  const slack_user_token = getFieldMeta("caroline.slack_user_token");
  const slack_bot_token = getFieldMeta("caroline.slack_bot_token");
  const slack_bot_id = getFieldMeta("caroline.slack_bot_id");
  const slack_app_user_token = getFieldMeta("caroline.slack_app_user_token");

  const [showChatKey, setShowChatKey] = useState(false);
  const [showSearchKey, setShowSearchKey] = useState(false);
  const [showSlackSecret, setShowSlackSecret] = useState(false);
  const [showSlackBotToken, setShowSlackBotToken] = useState(false);
  const [showSlackAppUserToken, setShowSlackAppUserToken] = useState(false);
  const [showContainerSasKey, setShowContainerSasKey] = useState(false);

  if (values.caroline === undefined) return <></>;

  const channelsWhiteListMeta: FieldMetaProps<string>[] = [];

  for (let i = 0; i < values.caroline.slack_channels_whitelist.length; i++) {
    const meta = `caroline.slack_channels_whitelist[${i}]`;
    channelsWhiteListMeta.push(getFieldMeta(`${meta}`));
  }

  const channelsWithDalle: FieldMetaProps<string>[] = [];

  for (let i = 0; i < values.caroline.slack_channels_dalle.length; i++) {
    const meta = `caroline.slack_channels_dalle[${i}]`;
    channelsWithDalle.push(getFieldMeta(`${meta}`));
  }

  return (
    <>
      <Card sx={{ width: "100%" }}>
        <CardHeader
          title="Caroline"
          titleTypographyProps={{ color: "primary" }}
        />
        <CardContent
          sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
        >
          <Field name="caroline.azure_chat_url" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Chat URL"
                error={azure_chat_url.touched && Boolean(azure_chat_url.error)}
                helperText={
                  azure_chat_url.touched &&
                  Boolean(azure_chat_url.error) &&
                  azure_chat_url.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_chat_key" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    azure_chat_key.touched && Boolean(azure_chat_key.error)
                  }
                >
                  Azure Chat Key
                </InputLabel>
                <OutlinedInput
                  label="Azure Chat Key"
                  type={showChatKey ? "text" : "password"}
                  endAdornment={
                    <IconButton onClick={() => setShowChatKey(!showChatKey)}>
                      {showChatKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  error={
                    azure_chat_key.touched && Boolean(azure_chat_key.error)
                  }
                  {...field}
                />
                {azure_chat_key.touched && Boolean(azure_chat_key.error) && (
                  <FormHelperText error={true}>
                    {azure_chat_key.error}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          </Field>

          <Field name="caroline.azure_chat_deployment" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Chat Deployment"
                error={
                  azure_chat_deployment.touched &&
                  Boolean(azure_chat_deployment.error)
                }
                helperText={
                  azure_chat_deployment.touched &&
                  Boolean(azure_chat_deployment.error) &&
                  azure_chat_deployment.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_chat_api_version" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Chat API Version"
                error={
                  azure_chat_api_version.touched &&
                  Boolean(azure_chat_api_version.error)
                }
                helperText={
                  azure_chat_api_version.touched &&
                  Boolean(azure_chat_api_version.error) &&
                  azure_chat_api_version.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field
            name="caroline.azure_chat_embedding_deployment"
            onChange={handleChange}
          >
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Chat Embedding Deployment"
                error={
                  azure_chat_embedding_deployment.touched &&
                  Boolean(azure_chat_embedding_deployment.error)
                }
                helperText={
                  azure_chat_embedding_deployment.touched &&
                  Boolean(azure_chat_embedding_deployment.error) &&
                  azure_chat_embedding_deployment.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_search_url" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Search URL"
                error={
                  azure_search_url.touched && Boolean(azure_search_url.error)
                }
                helperText={
                  azure_search_url.touched &&
                  Boolean(azure_search_url.error) &&
                  azure_search_url.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_search_key" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    azure_search_key.touched && Boolean(azure_search_key.error)
                  }
                >
                  Azure Search Key
                </InputLabel>
                <OutlinedInput
                  label="Azure Search Key"
                  type={showSearchKey ? "text" : "password"}
                  endAdornment={
                    <IconButton
                      onClick={() => setShowSearchKey(!showSearchKey)}
                    >
                      {showSearchKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  error={
                    azure_search_key.touched && Boolean(azure_search_key.error)
                  }
                  {...field}
                />
                {azure_search_key.touched &&
                  Boolean(azure_search_key.error) && (
                    <FormHelperText error={true}>
                      {azure_search_key.error}
                    </FormHelperText>
                  )}
              </FormControl>
            )}
          </Field>

          <Field name="caroline.azure_search_index" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Search Index"
                error={
                  azure_search_index.touched &&
                  Boolean(azure_search_index.error)
                }
                helperText={
                  azure_search_index.touched &&
                  Boolean(azure_search_index.error) &&
                  azure_search_index.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_dalle_deployment" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Dalle Deployment"
                error={
                  azure_dalle_deployment.touched &&
                  Boolean(azure_dalle_deployment.error)
                }
                helperText={
                  azure_dalle_deployment.touched &&
                  Boolean(azure_dalle_deployment.error) &&
                  azure_dalle_deployment.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field
            name="caroline.azure_dalle_api_version"
            onChange={handleChange}
          >
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Dalle API Version"
                error={
                  azure_dalle_api_version.touched &&
                  Boolean(azure_dalle_api_version.error)
                }
                helperText={
                  azure_dalle_api_version.touched &&
                  Boolean(azure_dalle_api_version.error) &&
                  azure_dalle_api_version.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_container_url" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Container URL"
                error={
                  azure_container_url.touched &&
                  Boolean(azure_container_url.error)
                }
                helperText={
                  azure_container_url.touched &&
                  Boolean(azure_container_url.error) &&
                  azure_container_url.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.azure_container_name" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Container name"
                error={
                  azure_container_name.touched &&
                  Boolean(azure_container_name.error)
                }
                helperText={
                  azure_container_name.touched &&
                  Boolean(azure_container_name.error) &&
                  azure_container_name.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field
            name="caroline.azure_container_sas_key"
            onChange={handleChange}
          >
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    azure_container_sas_key.touched &&
                    Boolean(azure_container_sas_key.error)
                  }
                >
                  Azure Container SAS Key
                </InputLabel>
                <OutlinedInput
                  label="Azure Container SAS Key"
                  type={showContainerSasKey ? "text" : "password"}
                  endAdornment={
                    <IconButton
                      onClick={() =>
                        setShowContainerSasKey(!showContainerSasKey)
                      }
                    >
                      {showContainerSasKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  error={
                    azure_container_sas_key.touched &&
                    Boolean(azure_container_sas_key.error)
                  }
                  {...field}
                />
                {azure_container_sas_key.touched &&
                  Boolean(azure_container_sas_key.error) && (
                    <FormHelperText error={true}>
                      {azure_container_sas_key.error}
                    </FormHelperText>
                  )}
              </FormControl>
            )}
          </Field>

          <Field name="caroline.azure_indexer_name" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Indexer name"
                error={
                  azure_indexer_name.touched &&
                  Boolean(azure_indexer_name.error)
                }
                helperText={
                  azure_indexer_name.touched &&
                  Boolean(azure_indexer_name.error) &&
                  azure_indexer_name.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field
            name="caroline.azure_indexer_api_version"
            onChange={handleChange}
          >
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Azure Indexer API version"
                error={
                  azure_indexer_api_version.touched &&
                  Boolean(azure_indexer_api_version.error)
                }
                helperText={
                  azure_indexer_api_version.touched &&
                  Boolean(azure_indexer_api_version.error) &&
                  azure_indexer_api_version.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.slack_app_secret" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    slack_app_secret.touched && Boolean(slack_app_secret.error)
                  }
                >
                  Slack App Secret
                </InputLabel>
                <OutlinedInput
                  label="Slack App Secret"
                  type={showSlackSecret ? "text" : "password"}
                  endAdornment={
                    <IconButton
                      onClick={() => setShowSlackSecret(!showSlackSecret)}
                    >
                      {showSlackSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  error={
                    slack_app_secret.touched && Boolean(slack_app_secret.error)
                  }
                  {...field}
                />
                {slack_app_secret.touched &&
                  Boolean(slack_app_secret.error) && (
                    <FormHelperText error={true}>
                      {slack_app_secret.error}
                    </FormHelperText>
                  )}
              </FormControl>
            )}
          </Field>

          <Field name="caroline.slack_user_token" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    slack_user_token.touched && Boolean(slack_user_token.error)
                  }
                >
                  Slack User Token
                </InputLabel>
                <OutlinedInput
                  label="Slack User Token"
                  type={showSlackSecret ? "text" : "password"}
                  endAdornment={
                    <IconButton
                      onClick={() => setShowSlackSecret(!showSlackSecret)}
                    >
                      {showSlackSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  error={
                    slack_user_token.touched && Boolean(slack_user_token.error)
                  }
                  {...field}
                />
                {slack_user_token.touched &&
                  Boolean(slack_user_token.error) && (
                    <FormHelperText error={true}>
                      {slack_user_token.error}
                    </FormHelperText>
                  )}
              </FormControl>
            )}
          </Field>

          <Field name="caroline.slack_bot_token" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    slack_bot_token.touched && Boolean(slack_bot_token.error)
                  }
                >
                  Slack Bot Token
                </InputLabel>
                <OutlinedInput
                  label="Slack Bot Token"
                  type={showSlackBotToken ? "text" : "password"}
                  endAdornment={
                    <IconButton
                      onClick={() => setShowSlackBotToken(!showSlackBotToken)}
                    >
                      {showSlackBotToken ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  error={
                    slack_bot_token.touched && Boolean(slack_bot_token.error)
                  }
                  {...field}
                />
                {slack_bot_token.touched && Boolean(slack_bot_token.error) && (
                  <FormHelperText error={true}>
                    {slack_bot_token.error}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          </Field>

          <Field name="caroline.slack_bot_id" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <TextField
                label="Slack Bot ID"
                error={slack_bot_id.touched && Boolean(slack_bot_id.error)}
                helperText={
                  slack_bot_id.touched &&
                  Boolean(slack_bot_id.error) &&
                  slack_bot_id.error
                }
                disabled={loading}
                {...field}
              />
            )}
          </Field>

          <Field name="caroline.slack_app_user_token" onChange={handleChange}>
            {({ field }: { field: FieldInputProps<unknown> }) => (
              <FormControl disabled={loading}>
                <InputLabel
                  error={
                    slack_app_user_token.touched &&
                    Boolean(slack_app_user_token.error)
                  }
                >
                  Slack App User Token
                </InputLabel>
                <OutlinedInput
                  label="Slack App User Token"
                  type={showSlackAppUserToken ? "text" : "password"}
                  endAdornment={
                    <IconButton
                      onClick={() =>
                        setShowSlackAppUserToken(!showSlackAppUserToken)
                      }
                    >
                      {showSlackAppUserToken ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  }
                  error={
                    slack_app_user_token.touched &&
                    Boolean(slack_app_user_token.error)
                  }
                  {...field}
                />
                {slack_app_user_token.touched &&
                  Boolean(slack_app_user_token.error) && (
                    <FormHelperText error={true}>
                      {slack_app_user_token.error}
                    </FormHelperText>
                  )}
              </FormControl>
            )}
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <FieldArray
            name="caroline.slack_channels_whitelist"
            render={(arrayHelpers) => (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    Slack Channels Whitelist
                  </Typography>
                  <Button
                    onClick={() => arrayHelpers.insert(0, "")}
                    disabled={loading}
                    color="secondary"
                  >
                    Add Channel
                  </Button>
                </Box>

                <br />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {values.caroline?.slack_channels_whitelist.map(
                    (channel, i) => (
                      <Box
                        key={i}
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                            alignItems: "baseline",
                          }}
                        >
                          <Field
                            name={`caroline.slack_channels_whitelist[${i}]`}
                            onChange={handleChange}
                          >
                            {({
                              field,
                            }: {
                              field: FieldInputProps<unknown>;
                            }) => (
                              <TextField
                                label="Channel Id"
                                error={
                                  channelsWhiteListMeta[i].touched &&
                                  Boolean(channelsWhiteListMeta[i].error)
                                }
                                helperText={
                                  channelsWhiteListMeta[i].touched &&
                                  Boolean(channelsWhiteListMeta[i].error) &&
                                  channelsWhiteListMeta[i].error
                                }
                                disabled={loading}
                                size="small"
                                fullWidth
                                {...field}
                              />
                            )}
                          </Field>
                        </Box>
                        <IconButton
                          onClick={() => arrayHelpers.remove(i)}
                          disabled={loading}
                        >
                          <Delete color={loading ? "disabled" : "error"} />
                        </IconButton>
                      </Box>
                    )
                  )}
                </Box>
              </>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <FieldArray
            name="caroline.slack_channels_dalle"
            render={(arrayHelpers) => (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    Slack Channels with Dalle
                  </Typography>
                  <Button
                    onClick={() => arrayHelpers.insert(0, "")}
                    disabled={loading}
                    color="secondary"
                  >
                    Add Channel
                  </Button>
                </Box>

                <br />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {values.caroline?.slack_channels_dalle.map((channel, i) => (
                    <Box
                      key={i}
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          width: "100%",
                          alignItems: "baseline",
                        }}
                      >
                        <Field
                          name={`caroline.slack_channels_dalle[${i}]`}
                          onChange={handleChange}
                        >
                          {({ field }: { field: FieldInputProps<unknown> }) => (
                            <TextField
                              label="Channel Id"
                              error={
                                channelsWithDalle[i].touched &&
                                Boolean(channelsWithDalle[i].error)
                              }
                              helperText={
                                channelsWithDalle[i].touched &&
                                Boolean(channelsWithDalle[i].error) &&
                                channelsWithDalle[i].error
                              }
                              disabled={loading}
                              size="small"
                              fullWidth
                              {...field}
                            />
                          )}
                        </Field>
                      </Box>
                      <IconButton
                        onClick={() => arrayHelpers.remove(i)}
                        disabled={loading}
                      >
                        <Delete color={loading ? "disabled" : "error"} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          />
        </CardContent>
      </Card>
    </>
  );
}


function ItemEntra(props: { loading: boolean }) {
  const loading = props.loading;

  const { values, getFieldMeta, handleChange } = useFormikContext<Item>();

  if (values.entra_client === undefined) return <></>;

  const login_url = getFieldMeta("entra_client.login_url");
  const api_url = getFieldMeta("entra_client.api_url");
  const client_id = getFieldMeta("entra_client.client_id");
  const client_secret = getFieldMeta("entra_client.client_secret");
  const tenant_id = getFieldMeta("entra_client.tenant_id");
  const grant_type = getFieldMeta("entra_client.grant_type");
  const scope = getFieldMeta("entra_client.scope");

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Entra Client"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >

        
        <Field name="entra_client.login_url" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              type="string"
              label="Login URL"
              error={login_url.touched && Boolean(login_url.error)}
              helperText={login_url.touched && Boolean(login_url.error) && login_url.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="entra_client.api_url" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              type="string"
              label="API URL"
              error={api_url.touched && Boolean(api_url.error)}
              helperText={api_url.touched && Boolean(api_url.error) && api_url.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="entra_client.client_id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              type="string"
              label="Client ID"
              error={client_id.touched && Boolean(client_id.error)}
              helperText={client_id.touched && Boolean(client_id.error) && client_id.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="entra_client.client_secret" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={client_secret.touched && Boolean(client_secret.error)}>
                Client Secret
              </InputLabel>
              <OutlinedInput
                label="Client Secret"
                type="password"
                error={client_secret.touched && Boolean(client_secret.error)}
                {...field}
              />
              {client_secret.touched && Boolean(client_secret.error) && (
                <FormHelperText error={true}>{client_secret.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>

        <Field name="entra_client.tenant_id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Tenant ID"
              error={tenant_id.touched && Boolean(tenant_id.error)}
              helperText={tenant_id.touched && Boolean(tenant_id.error) && tenant_id.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="entra_client.grant_type" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Grant Type"
              error={grant_type.touched && Boolean(grant_type.error)}
              helperText={grant_type.touched && Boolean(grant_type.error) && grant_type.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="entra_client.scope" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Scope"
              error={scope.touched && Boolean(scope.error)}
              helperText={scope.touched && Boolean(scope.error) && scope.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

      </CardContent>
    </Card>
  );
}


function ItemTableauEntraGroups(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();

  const creator_group = getFieldMeta("tableau_entra_groups.creator_group");
  const explorer_group = getFieldMeta("tableau_entra_groups.explorer_group");
  const viewer_group = getFieldMeta("tableau_entra_groups.viewer_group");

  return (
    <Card>
      <CardHeader
        title="Entra Tableau Groups"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="tableau_entra_groups.creator_group" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Creator Group ID"
              placeholder="string"
              error={creator_group.touched && Boolean(creator_group.error)}
              helperText={
                creator_group.touched && Boolean(creator_group.error) && creator_group.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="tableau_entra_groups.explorer_group" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Explorer Group ID"
              placeholder="string"
              error={explorer_group.touched && Boolean(explorer_group.error)}
              helperText={
                explorer_group.touched &&
                Boolean(explorer_group.error) &&
                explorer_group.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="tableau_entra_groups.viewer_group" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Viewr Group ID"
              placeholder="string"
              error={viewer_group.touched && Boolean(viewer_group.error)}
              helperText={
                viewer_group.touched &&
                Boolean(viewer_group.error) &&
                viewer_group.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

      </CardContent>
    </Card>
  );
}

function ItemTableauDetails(props: { loading: boolean }) {
  const loading = props.loading;

  const { values, getFieldMeta, handleChange, setFieldValue } =
    useFormikContext<Item>();

  const patName = getFieldMeta("tableau_details.pat_name");
  const patSecret = getFieldMeta("tableau_details.pat_secret");
  const patSiteContentUrl = getFieldMeta(
    "tableau_details.pat_site_content_url"
  );
  const patExpirationDate = getFieldMeta("tableau_details.pat_expiration_date");
  const onboardingGroup = getFieldMeta("tableau_details.onboarding_group");

  const [showSecret, setShowSecret] = useState(false);

  if (values.tableau_details === undefined) return <></>;

  const patExpirationDateValue = dayjs(
    values.tableau_details.pat_expiration_date
  );

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Tableau Details"
        titleTypographyProps={{ color: "primary" }}
      />

      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="tableau_details.pat_name" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="PAT Name"
              error={patName.touched && Boolean(patName.error)}
              helperText={
                patName.touched && Boolean(patName.error) && patName.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="tableau_details.pat_secret" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={patSecret.touched && Boolean(patSecret.error)}>
                PAT Secret
              </InputLabel>
              <OutlinedInput
                label="PAT Secret"
                type={showSecret ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowSecret(!showSecret)}>
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={patSecret.touched && Boolean(patSecret.error)}
                {...field}
              />
              {patSecret.touched && Boolean(patSecret.error) && (
                <FormHelperText error={true}>{patSecret.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>

        <Field
          name="tableau_details.pat_site_content_url"
          onChange={handleChange}
        >
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="PAT Site Content URL"
              error={
                patSiteContentUrl.touched && Boolean(patSiteContentUrl.error)
              }
              helperText={
                patSiteContentUrl.touched &&
                Boolean(patSiteContentUrl.error) &&
                patSiteContentUrl.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <DatePicker
          label="PAT Expiration Date"
          format="DD/MM/YYYY"
          disabled={loading}
          value={patExpirationDateValue}
          onChange={(date) =>
            setFieldValue(
              "tableau_details.pat_expiration_date",
              date?.toISOString()
            )
          }
          slotProps={{
            textField: {
              helperText:
                patExpirationDate.touched && Boolean(patExpirationDate.error)
                  ? patExpirationDate.error
                  : undefined,
              error:
                patExpirationDate.touched && Boolean(patExpirationDate.error),
            },
          }}
        />

        <Field name="tableau_details.onboarding_group" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Onboarding Group (UUID)"
              error={onboardingGroup.touched && Boolean(onboardingGroup.error)}
              helperText={
                onboardingGroup.touched &&
                Boolean(onboardingGroup.error) &&
                onboardingGroup.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemJiraDetails(props: { loading: boolean }) {
  const loading = props.loading;

  const { values, getFieldMeta, handleChange } = useFormikContext<Item>();

  if (values.jira_details === undefined) return <></>;

  const dirId = getFieldMeta("jira_details.directory_id");
  const read = getFieldMeta("jira_details.role_read");
  const write = getFieldMeta("jira_details.role_write");

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Jira Details"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="jira_details.directory_id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              type="number"
              label="Directory ID"
              error={dirId.touched && Boolean(dirId.error)}
              helperText={dirId.touched && Boolean(dirId.error) && dirId.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="jira_details.role_read" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Read Role"
              error={read.touched && Boolean(read.error)}
              helperText={read.touched && Boolean(read.error) && read.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="jira_details.role_write" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Write Role"
              error={write.touched && Boolean(write.error)}
              helperText={write.touched && Boolean(write.error) && write.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemJiraAccounts(props: { loading: boolean }) {
  const loading = props.loading;
  const [showPassword, setShowPassword] = useState(false);

  const { values, getFieldMeta, handleChange } = useFormikContext<Item>();

  if (values.jira_accounts === undefined) return <></>;

  const sdopUser = getFieldMeta("jira_accounts.sdop_user");
  const sdopPass = getFieldMeta("jira_accounts.sdop_pass");
  const vcdmUser = getFieldMeta("jira_accounts.vcdm_user");
  const vcdmPass = getFieldMeta("jira_accounts.vcdm_pass");

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Jira Accounts"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="jira_accounts.sdop_user" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="SDOP Account User"
              error={sdopUser.touched && Boolean(sdopUser.error)}
              helperText={
                sdopUser.touched && Boolean(sdopUser.error) && sdopUser.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="jira_accounts.sdop_pass" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={sdopPass.touched && Boolean(sdopPass.error)}>
                SDOP Account Password
              </InputLabel>
              <OutlinedInput
                label="SDOP Account Password"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={sdopPass.touched && Boolean(sdopPass.error)}
                {...field}
              />
              {sdopPass.touched && Boolean(sdopPass.error) && (
                <FormHelperText error={true}>{sdopPass.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>

        <Field name="jira_accounts.vcdm_user" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="VCDM Account User"
              error={vcdmUser.touched && Boolean(vcdmUser.error)}
              helperText={
                vcdmUser.touched && Boolean(vcdmUser.error) && vcdmUser.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="jira_accounts.vcdm_pass" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={vcdmPass.touched && Boolean(vcdmPass.error)}>
                VCDM Account Password
              </InputLabel>
              <OutlinedInput
                label="VCDM Account Password"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={vcdmPass.touched && Boolean(vcdmPass.error)}
                {...field}
              />
              {vcdmPass.touched && Boolean(vcdmPass.error) && (
                <FormHelperText error={true}>{vcdmPass.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemPolarisDetails(props: { loading: boolean }) {
  const loading = props.loading;

  const { values, getFieldMeta, handleChange } = useFormikContext<Item>();

  if (values.polaris_details === undefined) return <></>;

  const org_id = getFieldMeta("polaris_details.organization_id");

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Polaris Details"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="polaris_details.organization_id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Organization ID"
              error={org_id.touched && Boolean(org_id.error)}
              helperText={
                org_id.touched && Boolean(org_id.error) && org_id.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemLinks(props: { loading: boolean }) {
  const loading = props.loading;

  const { values, getFieldMeta, handleChange } = useFormikContext<Item>();

  if (values.links === undefined) return <></>;

  const meta: {
    name: FieldMetaProps<string>;
    url: FieldMetaProps<string>;
  }[] = [];

  for (let i = 0; i < values.links.length; i++) {
    const metaStep = `links[${i}]`;
    meta.push({
      name: getFieldMeta(`${metaStep}.name`),
      url: getFieldMeta(`${metaStep}.url`),
    });
  }

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Custom Links"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <FieldArray
          name="links"
          render={(arrayHelpers) => (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">
                  Links are ordered top to bottom
                </Typography>
                <Button
                  onClick={() => arrayHelpers.push({ name: "", url: "" })}
                  disabled={loading}
                  color="secondary"
                >
                  Add Link
                </Button>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {values.links?.map((step, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <OrderAdornment index={i} arrayHelpers={arrayHelpers} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "100%",
                        alignItems: "baseline",
                      }}
                    >
                      <Field name={`links[${i}].name`} onChange={handleChange}>
                        {({ field }: { field: FieldInputProps<unknown> }) => (
                          <TextField
                            label="Name"
                            error={
                              meta[i].name.touched &&
                              Boolean(meta[i].name.error)
                            }
                            helperText={
                              meta[i].name.touched &&
                              Boolean(meta[i].name.error) &&
                              meta[i].name.error
                            }
                            disabled={loading}
                            size="small"
                            fullWidth
                            {...field}
                          />
                        )}
                      </Field>

                      <Field name={`links[${i}].url`} onChange={handleChange}>
                        {({ field }: { field: FieldInputProps<unknown> }) => (
                          <TextField
                            label="URL"
                            error={
                              meta[i].url.touched && Boolean(meta[i].url.error)
                            }
                            helperText={
                              meta[i].url.touched &&
                              Boolean(meta[i].url.error) &&
                              meta[i].url.error
                            }
                            disabled={loading}
                            size="small"
                            fullWidth
                            {...field}
                          />
                        )}
                      </Field>
                    </Box>
                    <IconButton
                      onClick={() => arrayHelpers.remove(i)}
                      disabled={loading}
                    >
                      <Delete color={loading ? "disabled" : "error"} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </>
          )}
        />
      </CardContent>
    </Card>
  );
}

function ItemClient(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();
  const [showPassword, setShowPassword] = useState(false);

  const url = getFieldMeta("client.url");
  const domain = getFieldMeta("client.domain");
  const user = getFieldMeta("client.user");
  const pass = getFieldMeta("client.pass");

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader title="Client" titleTypographyProps={{ color: "primary" }} />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="client.url" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="URL"
              error={url.touched && Boolean(url.error)}
              helperText={url.touched && Boolean(url.error) && url.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>
        <Field name="client.user" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="User"
              error={user.touched && Boolean(user.error)}
              helperText={user.touched && Boolean(user.error) && user.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>
        <Field name="client.pass" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={pass.touched && Boolean(pass.error)}>
                Password
              </InputLabel>
              <OutlinedInput
                label="Password"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={pass.touched && Boolean(pass.error)}
                {...field}
              />
              {pass.touched && Boolean(pass.error) && (
                <FormHelperText error={true}>{pass.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>
        <Field name="client.domain" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Domain"
              error={domain.touched && Boolean(domain.error)}
              helperText={
                domain.touched && Boolean(domain.error) && domain.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemAutomation(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();
  const [showPassword, setShowPassword] = useState(false);

  const api_key = getFieldMeta("automation.api_key");

  return (
    <Card>
      <CardHeader
        title="Automation"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="automation.api_key" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={api_key.touched && Boolean(api_key.error)}>
                API Key
              </InputLabel>
              <OutlinedInput
                label="API Key"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={api_key.touched && Boolean(api_key.error)}
                {...field}
              />
              {api_key.touched && Boolean(api_key.error) && (
                <FormHelperText error={true}>{api_key.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemClientApiKey(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();
  const [showPassword, setShowPassword] = useState(false);

  const url = getFieldMeta("client_api_key.url");
  const api_key = getFieldMeta("client_api_key.api_key");

  return (
    <Card>
      <CardHeader
        title="Client with API Key"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="client_api_key.url" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="URL"
              error={url.touched && Boolean(url.error)}
              helperText={url.touched && Boolean(url.error) && url.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>
        <Field name="client_api_key.api_key" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={api_key.touched && Boolean(api_key.error)}>
                API Key
              </InputLabel>
              <OutlinedInput
                label="API Key"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={api_key.touched && Boolean(api_key.error)}
                {...field}
              />
              {api_key.touched && Boolean(api_key.error) && (
                <FormHelperText error={true}>{api_key.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemSimpleClient(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();

  const url = getFieldMeta("simple_client.url");

  return (
    <Card>
      <CardHeader
        title="Simple Client"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="simple_client.url" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="URL"
              error={url.touched && Boolean(url.error)}
              helperText={url.touched && Boolean(url.error) && url.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemEmail(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();
  const [showPassword, setShowPassword] = useState(false);

  const user = getFieldMeta("email.user");
  const pass = getFieldMeta("email.pass");
  const domain = getFieldMeta("email.domain");
  const sender = getFieldMeta("email.sender");
  const base_url = getFieldMeta("email.base_url");
  const admin_email = getFieldMeta("email.admin_email");

  return (
    <Card>
      <CardHeader title="Email" titleTypographyProps={{ color: "primary" }} />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="email.user" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Username"
              error={user.touched && Boolean(user.error)}
              helperText={user.touched && Boolean(user.error) && user.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="email.pass" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel error={pass.touched && Boolean(pass.error)}>
                Password
              </InputLabel>
              <OutlinedInput
                label="Password"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={pass.touched && Boolean(pass.error)}
                {...field}
              />
              {pass.touched && Boolean(pass.error) && (
                <FormHelperText error={true}>{pass.error}</FormHelperText>
              )}
            </FormControl>
          )}
        </Field>

        <Field name="email.domain" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Domain"
              error={domain.touched && Boolean(domain.error)}
              helperText={
                domain.touched && Boolean(domain.error) && domain.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="email.sender" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Sender"
              error={sender.touched && Boolean(sender.error)}
              helperText={
                sender.touched && Boolean(sender.error) && sender.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="email.base_url" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Base URL"
              error={base_url.touched && Boolean(base_url.error)}
              helperText={
                base_url.touched && Boolean(base_url.error) && base_url.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="email.admin_email" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Administator Email"
              error={admin_email.touched && Boolean(admin_email.error)}
              helperText={
                admin_email.touched &&
                Boolean(admin_email.error) &&
                admin_email.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

export function ItemGroups(props: {
  displayName?: string;
  fieldName: string;
  groupName: string;
  loading: boolean;
}) {
  const { values, handleChange, getFieldMeta } = useFormikContext<Item>();

  const { displayName, fieldName, groupName, loading } = props;

  const title =
    displayName !== undefined
      ? `${displayName} ${groupName} groups`
      : `${groupName.charAt(0).toUpperCase()}${groupName.slice(1)} groups`;

  const meta: FieldMetaProps<string>[] = [];

  for (
    let i = 0;
    i <
    (
      (values as unknown as Record<string, unknown>)[fieldName] as Record<
        string,
        string[]
      >
    )[groupName].length;
    i++
  )
    meta.push(getFieldMeta(`${fieldName}.${groupName}[${i}]`));

  return (
    <FieldArray
      name={`${fieldName}.${groupName}`}
      render={(arrayHelpers) => (
        <Card>
          <CardHeader
            title={title}
            action={
              <IconButton
                onClick={() => arrayHelpers.insert(0, "")}
                sx={{ marginRight: 1 }}
                disabled={loading}
              >
                <Add color={loading ? "disabled" : undefined} />
              </IconButton>
            }
            titleTypographyProps={{ color: "primary" }}
            subheader={
              <Typography variant="subtitle2" color="text.secondary">
                {groupName === "primary"
                  ? "Used for onboarding and offboarding"
                  : "Used for offboarding in addition to the primary groups"}
              </Typography>
            }
          />
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {(
              (values as unknown as Record<string, unknown>)[
                fieldName
              ] as Record<string, string[]>
            )[groupName]?.map((group, i) => (
              <FormControl key={i} disabled={loading}>
                <OutlinedInput
                  placeholder="Group"
                  name={`${fieldName}.${groupName}[${i}]`}
                  value={group}
                  onChange={handleChange}
                  size="small"
                  fullWidth
                  endAdornment={
                    <IconButton
                      onClick={() => arrayHelpers.remove(i)}
                      disabled={loading}
                    >
                      <Delete color={loading ? "disabled" : "error"} />
                    </IconButton>
                  }
                  error={meta[i].touched && Boolean(meta[i].error)}
                />
                {meta[i].touched && Boolean(meta[i].error) && (
                  <FormHelperText error={true}>{meta[i].error}</FormHelperText>
                )}
              </FormControl>
            ))}
          </CardContent>
        </Card>
      )}
    />
  );
}

function ItemMimGroup(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();

  const id = getFieldMeta("mim_group.id");

  return (
    <Card>
      <CardHeader
        title="MIM Group"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="mim_group.id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              placeholder="Uuid"
              error={id.touched && Boolean(id.error)}
              helperText={id.touched && Boolean(id.error) && id.error}
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemMultipleMimGroup(props: { loading: boolean; group_type: string }) {
  const { loading, group_type } = props;

  const { getFieldMeta, handleChange } = useFormikContext();

  const group = getFieldMeta(`multiple_mim_groups.${group_type}`);

  const group_title = group_type === "read_only_group" ? "Read only" : "Write";
  return (
    <Card>
      <CardHeader
        title={`MIM ${group_title} group`}
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field
          name={`multiple_mim_groups.${group_type}`}
          onChange={handleChange}
        >
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              placeholder="Uuid"
              error={group.touched && Boolean(group.error)}
              helperText={group.touched && Boolean(group.error) && group.error}
              disabled={loading}
              {...field} 
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemMimDetails(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();

  const owner_id = getFieldMeta("mim_details.owner_id");
  const company_id = getFieldMeta("mim_details.company_id");

  return (
    <Card>
      <CardHeader
        title="MIM Details"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="mim_details.owner_id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Owner Id"
              placeholder="Uuid"
              error={owner_id.touched && Boolean(owner_id.error)}
              helperText={
                owner_id.touched && Boolean(owner_id.error) && owner_id.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>

        <Field name="mim_details.company_id" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              label="Company Id"
              placeholder="Uuid"
              error={company_id.touched && Boolean(company_id.error)}
              helperText={
                company_id.touched &&
                Boolean(company_id.error) &&
                company_id.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemHooksVom(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();

  const verification_token = getFieldMeta("hooks_vom.verification_token");
  const secret = getFieldMeta("hooks_vom.secret");

  const [showToken, setShowToken] = useState(false);
  const [showChange, setShowChange] = useState(false);

  return (
    <Card>
      <CardHeader
        title="VOM Hooks"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="hooks_vom.verification_token" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel
                error={
                  verification_token.touched &&
                  Boolean(verification_token.error)
                }
              >
                Verification Token
              </InputLabel>
              <OutlinedInput
                label="Verification Token"
                type={showToken ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowToken(!showToken)}>
                    {showToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={
                  verification_token.touched &&
                  Boolean(verification_token.error)
                }
                {...field}
              />
              {verification_token.touched &&
                Boolean(verification_token.error) && (
                  <FormHelperText error={true}>
                    {verification_token.error}
                  </FormHelperText>
                )}
            </FormControl>
          )}
        </Field>

        <Field name="hooks_vom.secret" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <FormControl disabled={loading}>
              <InputLabel
                error={secret.touched && Boolean(secret.error)}
              >
                Secret
              </InputLabel>
              <OutlinedInput
                label="Secret"
                type={showChange ? "text" : "password"}
                endAdornment={
                  <IconButton onClick={() => setShowChange(!showChange)}>
                    {showChange ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
                error={secret.touched && Boolean(secret.error)}
                {...field}
              />
              {secret.touched && Boolean(secret.error) && (
                <FormHelperText error={true}>
                  {secret.error}
                </FormHelperText>
              )}
            </FormControl>
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemSchedule(props: { loading: boolean }) {
  const loading = props.loading;

  const { getFieldMeta, handleChange } = useFormikContext();

  const interval = getFieldMeta("schedule.interval_seconds");

  return (
    <Card>
      <CardHeader
        title="Schedule"
        titleTypographyProps={{ color: "primary" }}
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", padding: 2, gap: 2 }}
      >
        <Field name="schedule.interval_seconds" onChange={handleChange}>
          {({ field }: { field: FieldInputProps<unknown> }) => (
            <TextField
              type="number"
              label="Interval in seconds"
              error={interval.touched && Boolean(interval.error)}
              helperText={
                interval.touched && Boolean(interval.error) && interval.error
              }
              disabled={loading}
              {...field}
            />
          )}
        </Field>
      </CardContent>
    </Card>
  );
}

function ItemTool(props: { loading: boolean; kind: Tool }) {
  const { loading, kind } = props;

  const { values, handleChange, getFieldMeta, setFieldValue } =
    useFormikContext<Item>();

  const contractId = getFieldMeta("tool.contract_id");
  const linkMain = getFieldMeta("tool.links.main");

  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {values.tool !== undefined && (
        <>
          <Card>
            <CardHeader
              title="General information"
              titleTypographyProps={{ color: "primary" }}
            />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: 2,
                gap: 2,
              }}
            >
              <Avatar alt={kind} src={values.tool.avatar} />

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => {
                    const reader = new FileReader();
                    const file = e.target.files?.[0];

                    if (file) {
                      reader.onload = () =>
                        setFieldValue("tool.avatar", reader.result);
                      reader.readAsDataURL(file);
                    } else {
                      setFieldValue("tool.avatar", "");
                    }
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setFieldValue("tool.avatar", "");
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </Box>

              <Field name="tool.display_name" onChange={handleChange}>
                {({ field }: { field: FieldInputProps<unknown> }) => (
                  <TextField
                    label="Display Name"
                    {...field}
                    disabled={loading}
                  />
                )}
              </Field>

              <Field name="tool.short_desc" onChange={handleChange}>
                {({ field }: { field: FieldInputProps<unknown> }) => (
                  <TextField
                    label="Short Description"
                    {...field}
                    disabled={loading}
                  />
                )}
              </Field>

              <Field name="tool.long_desc" onChange={handleChange}>
                {({ field }: { field: FieldInputProps<unknown> }) => (
                  <TextField
                    label="Long Description"
                    {...field}
                    disabled={loading}
                    multiline
                    rows={4}
                  />
                )}
              </Field>

              <Field name="tool.contract_id" onChange={handleChange}>
                {({ field }: { field: FieldInputProps<unknown> }) => (
                  <TextField
                    label="Contract ID"
                    error={contractId.touched && Boolean(contractId.error)}
                    helperText={
                      contractId.touched &&
                      Boolean(contractId.error) &&
                      contractId.error
                    }
                    {...field}
                    disabled={loading}
                  />
                )}
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title="Pricing"
              titleTypographyProps={{ color: "primary" }}
            />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: 2,
                gap: 2,
              }}
            >
              <Field name="tool.pricing.desc" onChange={handleChange}>
                {({ field }: { field: FieldInputProps<unknown> }) => (
                  <TextField
                    label="Description"
                    {...field}
                    disabled={loading}
                    multiline
                    rows={2}
                  />
                )}
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title="Links"
              titleTypographyProps={{ color: "primary" }}
            />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: 2,
                gap: 2,
              }}
            >
              <Field name="tool.links.main" onChange={handleChange}>
                {({ field }: { field: FieldInputProps<unknown> }) => (
                  <TextField
                    label="Main"
                    error={linkMain.touched && Boolean(linkMain.error)}
                    helperText={
                      linkMain.touched &&
                      Boolean(linkMain.error) &&
                      linkMain.error
                    }
                    {...field}
                    disabled={loading}
                  />
                )}
              </Field>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
